"use client";

import { useState } from "react";
import { StaffPortfolioViewer } from "./StaffPortfolioViewer";

type Staff = {
  id: string;
  name: string;
  title?: string | null;
  avatarUrl?: string | null;
  portfolioImages?: { id: string; url: string }[];
};

type StaffSelectorProps = {
  staff: Staff[];
  value?: string; // staff id or "any"
  onChange: (staffId: string) => void;
  required?: boolean;
  error?: string;
  /** Whether to show the "Any Professional" option */
  showAnyOption?: boolean;
  label?: string;
};

export function StaffSelector({
  staff,
  value,
  onChange,
  required,
  error,
  showAnyOption = true,
  label = "Select Professional",
}: StaffSelectorProps) {
  const [portfolioStaff, setPortfolioStaff] = useState<Staff | null>(null);

  return (
    <div>
      <label className="block text-sm font-medium mb-2">
        {label} {required && <span className="text-destructive">*</span>}
      </label>

      <div className="grid gap-2">
        {/* Any Professional option */}
        {showAnyOption && (
          <button
            type="button"
            onClick={() => onChange("any")}
            className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-all ${
              value === "any"
                ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                : "border-input bg-background hover:bg-muted/50"
            }`}
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">Any Available Professional</p>
              <p className="text-xs text-muted-foreground">
                We&apos;ll match you with the first available
              </p>
            </div>
            {value === "any" && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </button>
        )}

        {/* Individual staff members */}
        {staff.map((s) => (
          <div
            key={s.id}
            className={`flex items-center gap-3 rounded-lg border p-3 transition-all ${
              value === s.id
                ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                : "border-input bg-background"
            }`}
          >
            <button
              type="button"
              onClick={() => onChange(s.id)}
              className="flex items-center gap-3 min-w-0 flex-1 text-left hover:opacity-90"
            >
              {s.avatarUrl ? (
                <img
                  src={s.avatarUrl}
                  alt={s.name}
                  className="h-10 w-10 shrink-0 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-medium text-sm">
                  {s.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">{s.name}</p>
                {s.title && (
                  <p className="text-xs text-muted-foreground">{s.title}</p>
                )}
              </div>
              {value === s.id && (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
            {s.portfolioImages && s.portfolioImages.length > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPortfolioStaff(s);
                }}
                className="shrink-0 text-xs font-medium text-primary hover:bg-primary hover:text-primary-foreground hover:no-underline py-1 px-2 rounded transition-colors"
              >
                View portfolio
              </button>
            )}
          </div>
        ))}
      </div>

      {staff.length === 0 && !showAnyOption && (
        <p className="text-sm text-muted-foreground mt-1">
          No professionals available for this selection.
        </p>
      )}

      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}

      <StaffPortfolioViewer
        isOpen={!!portfolioStaff}
        onClose={() => setPortfolioStaff(null)}
        staffName={portfolioStaff?.name ?? ""}
        portfolioImages={portfolioStaff?.portfolioImages ?? []}
      />
    </div>
  );
}
